import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import MarkdownBody from './MarkdownBody';
import CopyButton from './CopyButton';
import { insertItemAfter } from '../utils/markdown';

export function stripInlineMarkdown(text) {
  return text.replace(/\*\*(.+?)\*\*/g, '$1').replace(/__(.+?)__/g, '$1');
}

export function stripProposalTag(text) {
  return text.replace(/\s*\[\w+-proposal:[^\]]*\]/g, '');
}

// Fenced share blocks — the current share-proposal syntax (SHARE_V1):
// an opening `:::share <type>` line (type word optional), the shareable
// text (any markdown, ### headings included), a closing bare `:::` line.
// Fences replaced the legacy ### Share / ### Share type headings, which
// mis-parsed whenever the share body used ### itself and couldn't carry
// more than one post per node. Legacy headings still parse as a fallback.
const SHARE_FENCE_OPEN_RE = /^:::share(?:[ \t]+([A-Za-z]+))?[ \t]*\r?$/i;
const SHARE_FENCE_CLOSE_RE = /^:::[ \t]*\r?$/;

export function hasShareBlocks(text) {
  if (!text) return false;
  return text.split('\n').some(line => SHARE_FENCE_OPEN_RE.test(line));
}

// Returns [{ content, type }] — one entry per fenced block, in order.
// An unclosed block runs to the end of the text. With no fences present,
// falls back to the legacy headings (single share).
export function parseShareBlocks(text) {
  if (!text) return [];
  const shares = [];
  let current = null;
  const flush = () => {
    if (!current) return;
    const content = current.lines.join('\n').trim();
    if (content) shares.push({ content, type: current.type });
    current = null;
  };
  for (const line of text.split('\n')) {
    if (current) {
      if (SHARE_FENCE_CLOSE_RE.test(line)) flush();
      else current.lines.push(line);
      continue;
    }
    const open = line.match(SHARE_FENCE_OPEN_RE);
    if (open) current = { type: (open[1] || '').toLowerCase(), lines: [] };
  }
  flush();
  if (shares.length) return shares;
  const legacy = parseOrientResponse(text);
  if (legacy.share) return [{ content: legacy.share, type: legacy.shareType || '' }];
  return [];
}

export function parseTodoItems(text) {
  return text.split('\n')
    .map(l => l.replace(/^[-*]\s*/, '').replace(/^\[[ xX]\]\s*/, '').trim())
    .map(l => stripInlineMarkdown(l))
    .filter(Boolean);
}

export function parsePriorityItems(text) {
  return text.split('\n')
    .filter(l => l.trim())
    .map(l => {
      const cleaned = l.replace(/^\d+[.)]\s*/, '').replace(/^[-*]\s*/, '').replace(/^\[[ xX]\]\s*/, '').trim();
      const dashMatch = cleaned.match(/^(.+?)\s*[—–]\s*(.+)$/);
      if (dashMatch) return { text: stripInlineMarkdown(dashMatch[1].trim()), hint: dashMatch[2].trim() };
      const parenMatch = cleaned.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
      if (parenMatch) return { text: stripInlineMarkdown(parenMatch[1].trim()), hint: parenMatch[2].trim() };
      return { text: stripInlineMarkdown(cleaned), hint: '' };
    })
    .filter(item => item.text);
}

// Remove fenced :::share blocks entirely — their bodies may legitimately
// contain ### headings, which must never be parsed as todo/issue/feedback
// sections.
export function stripShareBlocks(text) {
  if (!text) return text;
  const out = [];
  let inFence = false;
  for (const line of text.split('\n')) {
    if (inFence) {
      if (SHARE_FENCE_CLOSE_RE.test(line)) inFence = false;
      continue;
    }
    if (SHARE_FENCE_OPEN_RE.test(line)) { inFence = true; continue; }
    out.push(line);
  }
  return out.join('\n');
}

export function parseOrientResponse(text) {
  const sections = {};
  const parts = stripShareBlocks(text).split(/^###\s+/m);
  // parts[0] is the lead-in before the first heading — never a section.
  // Parsing it used to misfire: a lead-in starting "Noted both — …"
  // matched includes('note') and became a phantom todo note.
  for (const part of parts.slice(1)) {
    if (!part.trim()) continue;
    const firstNewline = part.indexOf('\n');
    if (firstNewline < 0) continue;
    const heading = part.slice(0, firstNewline).trim().toLowerCase();
    const body = part.slice(firstNewline + 1).trim();
    if (heading.includes('completed')) sections.completed = body;
    else if (heading.includes('new task')) sections.newTasks = body;
    else if (heading.includes('priority')) sections.priority = body;
    else if (heading.includes('note')) sections.note = stripProposalTag(body);
    else if (heading.includes('issue title') || heading === 'title') sections.issueTitle = stripProposalTag(body).trim();
    else if (heading === 'description') sections.issueDescription = stripProposalTag(body).trim();
    // Category badges take the first line only — the model sometimes adds a
    // closing remark after the category value, which would otherwise leak into
    // the badge. The backend parsers (parse_github_issue / parse_feedback)
    // already take line one, so this just matches the display to the data.
    else if (heading === 'category') sections.issueCategory = stripProposalTag(body).split('\n')[0].trim().toLowerCase();
    else if (heading === 'feedback') sections.feedback = stripProposalTag(body).trim();
    else if (heading === 'feedback category') sections.feedbackCategory = stripProposalTag(body).split('\n')[0].trim().toLowerCase();
    else if (heading === 'share') sections.share = stripProposalTag(body).trim();
    else if (heading === 'share type') sections.shareType = stripProposalTag(body).split('\n')[0].trim().toLowerCase();
  }
  return sections;
}

export function hasProposalSections(text) {
  if (!text) return false;
  if (hasShareBlocks(text)) return true;
  // Heading scan runs on fence-stripped text — ### headings inside a
  // share body are the share's own formatting, not proposal sections.
  const headings = (stripShareBlocks(text).match(/^###\s+(.+)/gm) || []).map(h => h.replace(/^###\s+/, '').toLowerCase());
  const taskKeywords = ['completed', 'new task', 'new tasks', 'priority'];
  const hasTodo = headings.some(h => taskKeywords.some(kw => h.includes(kw)));
  const hasIssue = headings.some(h => h.includes('issue title') || h === 'title') &&
                   headings.some(h => h.includes('description'));
  const hasFeedback = headings.some(h => h === 'feedback');
  const hasShare = headings.some(h => h === 'share');
  return hasTodo || hasIssue || hasFeedback || hasShare;
}

// Headings for sections moveProposalItem has to create, keyed by the
// section keys the toggles pass. Order and casing follow the prompts
// (orient.txt / voice.txt / agentic.txt).
const PROPOSAL_SECTION_ORDER = ['completed', 'new task'];
const PROPOSAL_SECTION_TITLES = { completed: 'Completed', 'new task': 'New Tasks' };

/**
 * Move a todo-list item between `###` sections in raw LLM content.
 * Used by the interactive proposal toggles to flip a task between
 * "completed" and "new task" (or back again) and have the LLM content
 * reflect that decision so subsequent turns see the edited state.
 *
 * `fromSection` / `toSection` are lowercase substrings matched against
 * heading text (e.g. 'completed', 'new task').
 * `itemText` is the stripped display text to match raw lines against.
 * Returns the original content unchanged if the item can't be found.
 * If the target section doesn't exist (e.g. a proposal with only
 * `### New Tasks`), it is created next to the source section in the
 * prompts' order — Completed before New Tasks — so the item is never
 * dropped (#377).
 */
export function moveProposalItem(content, itemText, fromSection, toSection, { prepend = false } = {}) {
  const lines = content.split('\n');
  const sectionRegex = /^###\s+(.+)/;

  const sections = [];
  for (let i = 0; i < lines.length; i++) {
    const m = lines[i].match(sectionRegex);
    if (m) {
      if (sections.length > 0) sections[sections.length - 1].end = i;
      sections.push({ heading: m[1].trim().toLowerCase(), start: i, end: lines.length });
    }
  }
  if (sections.length > 0) sections[sections.length - 1].end = lines.length;

  const from = sections.find(s => s.heading.includes(fromSection));
  const to = sections.find(s => s.heading.includes(toSection));
  if (!from) return content;

  let matchIdx = -1;
  let rawLine = null;
  for (let i = from.start + 1; i < from.end; i++) {
    const stripped = stripInlineMarkdown(
      lines[i].replace(/^[-*]\s*/, '').replace(/^\[[ xX]\]\s*/, '').replace(/^\d+[.)]\s*/, '').trim()
    );
    if (stripped === itemText) {
      matchIdx = i;
      rawLine = lines[i];
      break;
    }
  }
  if (matchIdx < 0) return content;

  lines.splice(matchIdx, 1);

  if (to) {
    let toInsert = -1;
    for (let i = 0; i < lines.length; i++) {
      const m = lines[i].match(sectionRegex);
      if (m && m[1].trim().toLowerCase().includes(toSection)) {
        toInsert = i + 1;
        if (!prepend) {
          while (toInsert < lines.length && !lines[toInsert].match(sectionRegex) && lines[toInsert].trim()) {
            toInsert++;
          }
        }
        break;
      }
    }
    if (toInsert >= 0) {
      lines.splice(toInsert, 0, rawLine);
    }
  } else {
    const heading = `### ${PROPOSAL_SECTION_TITLES[toSection] || toSection}`;
    const rank = (key) => PROPOSAL_SECTION_ORDER.indexOf(key);
    if (rank(toSection) >= 0 && rank(toSection) < rank(fromSection)) {
      // Target comes first: new section goes right above the source heading.
      lines.splice(from.start, 0, heading, rawLine, '');
    } else {
      // Target comes after: new section goes below the source section's last
      // non-blank line (the item was removed, so its end shifted up by one).
      let last = from.start;
      for (let i = from.start + 1; i < from.end - 1; i++) {
        if (lines[i].trim()) last = i;
      }
      lines.splice(last + 1, 0, '', heading, rawLine);
    }
  }

  return lines.join('\n');
}

// Split a response that carries a proposal into the prose around it:
//   before — the lead-in shown above the proposal card,
//   after  — trailing commentary the model appended below the structured
//            block (it likes to sign off after the proposal).
// The structured sections themselves (lists / single values) are rendered in
// the card and excluded from both. Single-line-value sections (Category /
// Feedback category) consume only their one value line, so a following
// non-empty line is treated as the start of the trailing commentary. Todo
// dodges all this via its ### Note section, which renders inside the card.
// `shareOnly` (user-authored nodes) excludes only the :::share fences —
// ### headings there are the user's own formatting, not proposal sections.
export function splitProposalText(text, { shareOnly = false } = {}) {
  if (!text) return { before: '', after: '' };
  const lines = text.split('\n');
  const before = [];
  const after = [];
  let inProposal = false;
  let valueSection = false;
  let valueConsumed = false;
  let seenProposal = false;
  const proposalHeadings = ['completed', 'new task', 'new tasks', 'priority', 'priority order',
    'issue title', 'title', 'description', 'category', 'feedback'];
  // 'note' is a todo-card section ONLY alongside a task-specific heading —
  // the backend detector says the same ("a standalone ### Note is not
  // enough"). Without this, a prose heading like '### A note on process'
  // was swallowed here while no card rendered it: content vanished, and
  // the todo card showed a stray Apply button (seen on prod).
  const hasTaskSection = !shareOnly
    && /^###\s+.*(completed|new task|priority)/im.test(stripShareBlocks(text));
  if (hasTaskSection) proposalHeadings.push('note');
  // Share headings match EXACTLY ('share' / 'share type') — substring
  // matching would swallow unrelated sections like '### Shared context'.
  const exactProposalHeadings = ['share', 'share type'];
  const isValueHeading = (h) => h === 'category' || h === 'feedback category' || h === 'share type';
  const keep = (line) => (seenProposal ? after : before).push(line);
  let inShareFence = false;
  for (const line of lines) {
    // Fenced :::share blocks render in the proposal card — the fences and
    // everything between them are excluded from the surrounding prose.
    if (inShareFence) {
      if (SHARE_FENCE_CLOSE_RE.test(line)) inShareFence = false;
      continue;
    }
    if (SHARE_FENCE_OPEN_RE.test(line)) {
      inShareFence = true;
      inProposal = false;
      valueSection = false;
      seenProposal = true;
      continue;
    }
    const headingMatch = shareOnly ? null : line.match(/^###\s+(.+)/);
    if (headingMatch) {
      const h = headingMatch[1].trim().toLowerCase();
      if (proposalHeadings.some(kw => h.includes(kw) || h === kw)
          || exactProposalHeadings.includes(h)) {
        inProposal = true;
        valueSection = isValueHeading(h);
        valueConsumed = false;
        seenProposal = true;
        continue;
      } else {
        inProposal = false;
        valueSection = false;
      }
    }
    if (inProposal && valueSection) {
      if (line.trim() === '') {
        continue;  // drop blank lines around the value
      }
      if (!valueConsumed) {
        valueConsumed = true;
        continue;  // drop the single value line itself
      }
      // First non-empty line after the value = trailing commentary.
      inProposal = false;
      valueSection = false;
      keep(line);
      continue;
    }
    if (!inProposal) {
      keep(line);
    }
  }
  return {
    before: before.join('\n').trim(),
    after: after.join('\n').trim(),
  };
}

export function stripProposalSections(text) {
  if (!text) return text;
  const { before, after } = splitProposalText(text);
  return [before, after].filter(Boolean).join('\n\n');
}

// Per-variant styling. Compact (default) suits inline rendering under a
// highlighted card in NodeDetail. Roomy matches Voice mode's larger,
// more breathable layout for "playing back" a response at arm's length.
function sizeStyles(size) {
  const roomy = size === 'roomy';
  return {
    roomy,
    wrapper: roomy
      ? { width: '100%', maxWidth: '620px', marginTop: '32px' }
      : { marginTop: '8px', width: '100%' },
    sectionWrapper: roomy
      ? { marginBottom: '2.5rem' }
      : { marginBottom: '1rem' },
    sectionLabel: {
      fontSize: '0.68rem', letterSpacing: '0.18em', textTransform: 'uppercase',
      color: 'var(--accent)', opacity: 0.6,
      marginBottom: roomy ? '1.2rem' : '0.8rem',
      marginTop: roomy ? undefined : '1rem',
      display: 'flex', alignItems: 'center', gap: '8px',
      fontFamily: 'var(--sans)',
    },
    row: {
      display: 'flex', alignItems: 'flex-start',
      gap: roomy ? '12px' : '10px',
      padding: roomy ? '12px 0' : '8px 0',
      borderBottom: roomy ? '1px solid var(--bg-surface)' : '1px solid var(--border)',
    },
    circleBase: {
      width: roomy ? '18px' : '16px',
      height: roomy ? '18px' : '16px',
      borderRadius: '50%',
      flexShrink: 0,
      marginTop: '2px',
      transition: 'all 0.3s',
    },
    circleCompletedExtra: {
      border: '1.5px solid var(--accent-dim)',
      background: 'var(--accent-dim)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: roomy ? '0.6rem' : '0.55rem',
      color: 'var(--bg-deep)', fontWeight: 600,
    },
    circleNewExtra: {
      border: '1.5px solid var(--border-hover)',
    },
    itemText: {
      fontFamily: 'var(--sans)', fontWeight: 300,
      fontSize: roomy ? '0.92rem' : '0.85rem',
      color: 'var(--text-secondary)', lineHeight: 1.5,
    },
    priorityCard: {
      display: 'flex', alignItems: 'center',
      gap: roomy ? '14px' : '10px',
      padding: roomy ? '14px 16px' : '10px 12px',
      background: 'var(--bg-card)',
      border: '1px solid var(--border)',
      borderRadius: roomy ? '8px' : '6px',
      marginBottom: roomy ? '8px' : '6px',
      transition: roomy ? 'all 0.3s' : undefined,
      cursor: 'default',
      overflow: roomy ? 'hidden' : undefined,
    },
    priorityNumber: {
      fontFamily: 'var(--serif)',
      fontSize: roomy ? '1.4rem' : '1.1rem',
      color: 'var(--accent-dim)', opacity: 0.6,
      width: roomy ? '24px' : '20px',
      textAlign: 'center', flexShrink: 0,
    },
    priorityText: {
      fontFamily: 'var(--sans)', fontWeight: 300,
      fontSize: roomy ? '0.92rem' : '0.85rem',
      color: 'var(--text-primary)', lineHeight: 1.4,
      flex: '1 1 auto',
      minWidth: roomy ? '40%' : undefined,
      overflowWrap: roomy ? 'break-word' : undefined,
    },
    priorityHint: {
      fontSize: roomy ? '0.75rem' : '0.72rem',
      color: 'var(--text-muted)',
      flex: roomy ? '0 1 auto' : undefined,
      minWidth: roomy ? 0 : undefined,
      overflowWrap: roomy ? 'break-word' : undefined,
    },
    noteText: {
      fontFamily: 'var(--sans)', fontWeight: 300,
      fontSize: roomy ? '0.88rem' : '0.82rem',
      lineHeight: 1.7, color: 'var(--text-muted)',
      marginTop: roomy ? '2rem' : undefined,
      marginBottom: roomy ? undefined : '0.8rem',
    },
    applyWrapper: roomy
      ? { textAlign: 'center', marginTop: '24px' }
      : { marginTop: '8px' },
    button: {
      padding: roomy ? '10px 24px' : '7px 18px',
      background: 'none',
      border: '1px solid var(--accent)',
      borderRadius: '6px',
      color: 'var(--accent)',
      fontFamily: 'var(--sans)',
      fontSize: roomy ? '0.85rem' : '0.8rem',
      fontWeight: 400,
      cursor: 'pointer',
    },
    statusText: roomy
      ? { fontFamily: 'var(--sans)', fontSize: '0.75rem', fontWeight: 300 }
      : { fontFamily: 'var(--sans)', fontSize: '0.72rem' },
    statusTag: roomy ? 'p' : 'span',
    issueWrapper: roomy
      ? { width: '100%', maxWidth: '620px', marginTop: '32px' }
      : { marginTop: '12px' },
    issueCard: {
      padding: roomy ? '20px' : '14px',
      background: 'var(--bg-card)',
      border: '1px solid var(--border)',
      borderRadius: roomy ? '8px' : '6px',
    },
    issueTitleStyle: {
      fontFamily: 'var(--serif)',
      fontSize: roomy ? '1.2rem' : '1rem',
      color: 'var(--text-primary)',
      margin: roomy ? '0 0 12px 0' : '0 0 8px 0',
      fontWeight: 400,
    },
    issueDescStyle: {
      fontFamily: 'var(--sans)',
      fontSize: roomy ? '0.88rem' : '0.82rem',
      fontWeight: 300,
      color: 'var(--text-secondary)',
      lineHeight: 1.7,
      margin: roomy ? '0 0 12px 0' : undefined,
    },
    issueCategory: {
      display: 'inline-block',
      padding: roomy ? '3px 10px' : '2px 8px',
      fontSize: roomy ? '0.72rem' : '0.68rem',
      fontFamily: 'var(--sans)',
      fontWeight: 400, letterSpacing: '0.08em',
      textTransform: 'uppercase',
      borderRadius: roomy ? '12px' : '10px',
      border: '1px solid var(--accent)',
      color: 'var(--accent)', opacity: 0.8,
      marginTop: roomy ? undefined : '6px',
    },
    issueButtonWrapper: roomy
      ? { textAlign: 'center', marginTop: '16px' }
      : { marginTop: '8px' },
  };
}

// A single "New Tasks" proposal row with a hover "+" that opens an inline
// input to insert a sibling task right below it (Voice + Text modes share
// this — both render through the same row path).
// A single proposal row (Completed or New Tasks) with a hover "+" right after
// the text that opens an inline input to insert a sibling item below it.
// Voice + Text modes share this (only `styles` differ via `size`).
function ProposalTaskRow({ item, styles, toggleable, variant, addingKey, setAddingKey, onToggle, onInsert }) {
  const [hovered, setHovered] = useState(false);
  const [addText, setAddText] = useState('');
  const completed = variant === 'completed';
  const rowKey = `${variant}:${item}`;
  const adding = addingKey === rowKey;
  const close = () => { setAddingKey(null); setAddText(''); };
  const submit = () => {
    const t = addText.trim();
    if (t) onInsert(item, t);
    close();
  };
  return (
    <>
      <div
        style={styles.row}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div
          onClick={toggleable
            ? () => (completed
                ? onToggle(item, 'completed', 'new task', { prepend: true })
                : onToggle(item, 'new task', 'completed'))
            : undefined}
          title={toggleable ? (completed ? 'Unmark as done' : 'Mark as done') : undefined}
          style={{ ...styles.circleBase, ...(completed ? styles.circleCompletedExtra : styles.circleNewExtra), cursor: toggleable ? 'pointer' : 'default' }}
        >{completed ? '✓' : null}</div>
        <div style={{ ...styles.itemText, ...(completed ? { textDecoration: 'line-through', opacity: 0.4 } : null) }}>{item}</div>
        {toggleable && (
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); setAddText(''); setAddingKey(rowKey); }}
            onClick={() => { setAddText(''); setAddingKey(rowKey); }}
            title="Add an item below"
            aria-label="Add an item below"
            style={{
              opacity: hovered ? 1 : 0,
              transition: 'opacity 0.12s ease', background: 'none', border: 'none',
              color: 'var(--accent)', cursor: 'pointer', fontSize: '1.2rem',
              lineHeight: 1, padding: '0 4px',
            }}
          >+</button>
        )}
      </div>
      {adding && (
        <div style={{ display: 'flex', alignItems: 'center', gap: styles.roomy ? '12px' : '10px', padding: styles.roomy ? '12px 0' : '8px 0' }}>
          <span style={{ width: styles.roomy ? '18px' : '16px', height: styles.roomy ? '18px' : '16px', borderRadius: '50%', border: '1.5px dashed var(--border-hover)', flexShrink: 0, opacity: 0.5 }} />
          <input
            autoFocus
            value={addText}
            onChange={(e) => setAddText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.metaKey && !e.ctrlKey) { e.preventDefault(); submit(); }
              else if (e.key === 'Escape') { e.preventDefault(); close(); }
            }}
            onBlur={() => { if (!addText.trim()) close(); }}
            placeholder="New item…"
            style={{
              flex: 1, minWidth: 0, background: 'var(--bg-input)',
              border: '1px solid var(--border)', borderRadius: '6px',
              color: 'var(--text-primary)', fontFamily: 'var(--sans)',
              fontSize: styles.roomy ? '0.92rem' : '0.85rem', fontWeight: 300,
              padding: '8px 12px',
            }}
          />
        </div>
      )}
    </>
  );
}

export default function ProposalInline({
  content,
  nodeId,
  toolCallsMeta,
  onContentChange,
  onApplied,
  onError,
  size = 'compact',
  // User-authored nodes: only the :::share flow applies — the node's own
  // ### headings are the user's formatting, never todo/issue/feedback
  // proposals (whose confirm endpoints would 404 without a pending draft).
  shareOnly = false,
  // The proposal's owner (who asked for the reply) accepts it here. Anyone
  // else sees the proposal without the accept buttons and their status:
  // the accept endpoints only act on the owner's pending drafts, and
  // whether the owner accepted is theirs.
  canAct = true,
}) {
  const parsed = parseOrientResponse(content || '');
  // A note alone is NOT a todo proposal (matches the backend detector) —
  // otherwise any '### …note…' heading grew a stray Apply button.
  const hasTodo = parsed.completed || parsed.newTasks || parsed.priority;
  const hasIssue = parsed.issueTitle && parsed.issueDescription;
  const hasFeedback = !!parsed.feedback;
  const shares = parseShareBlocks(content || '');
  const hasShare = shares.length > 0;
  const [applyStatus, setApplyStatus] = useState(null);
  // Which row (if any) currently has its inline add-input open. Lifted here so
  // opening one row's "+" closes any other — and clicking a second "+" switches
  // to it instead of just cancelling the first.
  const [addingKey, setAddingKey] = useState(null);
  const [applyError, setApplyError] = useState(null);
  // A failed todo merge leaves the proposal applicable (the server's
  // `retryable`, #434): the card shows "Apply again" next to the error.
  const [canApplyAgain, setCanApplyAgain] = useState(false);
  const [issueApplyStatus, setIssueApplyStatus] = useState(null);
  const [issueApplyError, setIssueApplyError] = useState(null);
  const [issueResult, setIssueResult] = useState(null);
  const [feedbackApplyStatus, setFeedbackApplyStatus] = useState(null);
  const [feedbackApplyError, setFeedbackApplyError] = useState(null);
  // Per-share-block save state: idx -> 'started' | 'completed' | 'error'.
  // Each block has its own Save button; blocks already saved (per server
  // tool meta) come in via metaSavedShares ('all' or an index array).
  const [shareStates, setShareStates] = useState({});
  const [shareErrors, setShareErrors] = useState({});
  const [metaSavedShares, setMetaSavedShares] = useState(null);
  const mergePollingRef = useRef(null);
  const styles = sizeStyles(size);
  const toggleable = typeof onContentChange === 'function'
    && applyStatus !== 'completed'
    && applyStatus !== 'started';

  const handleToggleItem = useCallback((itemText, fromSection, toSection, opts) => {
    if (!toggleable || !nodeId) return;
    const newContent = moveProposalItem(content, itemText, fromSection, toSection, opts);
    if (newContent === content) return;
    const prevContent = content;
    onContentChange(newContent);
    api.put(`/nodes/${nodeId}`, { content: newContent }).catch((err) => {
      console.error('Failed to toggle proposal item:', err);
      onContentChange(prevContent);
      if (onError) {
        const reason = err.response?.data?.error || err.response?.statusText || err.message || 'Unknown error';
        onError(`Couldn't save change — reverted (${reason})`);
      }
    });
  }, [content, nodeId, onContentChange, onError, toggleable]);

  // Insert a new task immediately below a given "New Tasks" row (the hover "+"),
  // preserving the section's plain-bullet style. Same optimistic+revert path.
  const handleInsertAfter = useCallback((afterItem, newTask) => {
    const task = (newTask || '').trim();
    if (!toggleable || !nodeId || !task) return;
    const newContent = insertItemAfter(content, afterItem, task);
    if (newContent === content) return;
    const prevContent = content;
    onContentChange(newContent);
    api.put(`/nodes/${nodeId}`, { content: newContent }).catch((err) => {
      onContentChange(prevContent);
      if (onError) {
        const reason = err?.response?.data?.error || err?.message || 'Unknown error';
        onError(`Couldn't add task — reverted (${reason})`);
      }
    });
  }, [content, nodeId, onContentChange, onError, toggleable]);

  useEffect(() => {
    if (!toolCallsMeta) return;
    const todoEntry = toolCallsMeta.find(tc => tc.name === 'propose_todo');
    const applyTodoCall = toolCallsMeta.find(tc => tc.name === 'apply_todo_changes');
    if (todoEntry) {
      if (todoEntry.apply_status === 'completed') setApplyStatus('completed');
      else if (todoEntry.apply_status === 'failed') {
        setApplyStatus('error');
        setApplyError(todoEntry.apply_error || 'Todo merge failed');
        setCanApplyAgain(!!todoEntry.retryable);
      } else if (todoEntry.apply_status === 'started') {
        setApplyStatus('started');
      }
    } else if (applyTodoCall && applyTodoCall.status === 'success') {
      // Direct apply without a preceding proposal.
      setApplyStatus('completed');
    }
    const issueEntry = toolCallsMeta.find(tc => tc.name === 'propose_github_issue');
    const applyIssueCall = toolCallsMeta.find(tc => tc.name === 'apply_github_issue');
    if (issueEntry && issueEntry.apply_status === 'completed') {
      setIssueApplyStatus('completed');
      setIssueResult({ issue_url: issueEntry.issue_url, issue_number: issueEntry.issue_number });
    } else if (applyIssueCall && applyIssueCall.status === 'success') {
      setIssueApplyStatus('completed');
      setIssueResult({ issue_url: applyIssueCall.issue_url, issue_number: applyIssueCall.issue_number });
    }
    const feedbackEntry = toolCallsMeta.find(tc => tc.name === 'propose_feedback');
    const applyFeedbackCall = toolCallsMeta.find(tc => tc.name === 'apply_feedback');
    if (feedbackEntry && feedbackEntry.apply_status === 'completed') {
      setFeedbackApplyStatus('completed');
    } else if (applyFeedbackCall && applyFeedbackCall.status === 'success') {
      setFeedbackApplyStatus('completed');
    }
    const shareEntry = toolCallsMeta.find(tc => tc.name === 'propose_share');
    const applyShareCall = toolCallsMeta.find(tc => tc.name === 'apply_share');
    if ((shareEntry && shareEntry.apply_status === 'completed')
        || (applyShareCall && applyShareCall.status === 'success')) {
      setMetaSavedShares('all');
    } else if (shareEntry?.saved_indexes?.length) {
      setMetaSavedShares(shareEntry.saved_indexes);
    }
  }, [toolCallsMeta]);

  useEffect(() => {
    return () => {
      if (mergePollingRef.current) clearInterval(mergePollingRef.current);
    };
  }, []);

  const pollApplyStatus = useCallback((nId) => {
    if (mergePollingRef.current) clearInterval(mergePollingRef.current);
    mergePollingRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/nodes/${nId}/llm-status`);
        const meta = res.data.tool_calls_meta;
        if (meta) {
          const todoEntry = meta.find(tc => tc.name === 'propose_todo');
          if (todoEntry?.apply_status === 'completed') {
            clearInterval(mergePollingRef.current);
            setApplyStatus('completed');
            onApplied?.('propose_todo', { apply_status: 'completed' });
          } else if (todoEntry?.apply_status === 'failed') {
            clearInterval(mergePollingRef.current);
            setApplyStatus('error');
            setApplyError(todoEntry.apply_error || 'Todo merge failed');
            setCanApplyAgain(!!todoEntry.retryable);
            onApplied?.('propose_todo', {
              apply_status: 'failed',
              apply_error: todoEntry.apply_error || 'Todo merge failed',
              retryable: !!todoEntry.retryable,
            });
          }
        }
      } catch { /* keep polling */ }
    }, 2000);
  }, [onApplied]);

  const handleApplyTodo = useCallback(async () => {
    if (!nodeId) return;
    setApplyStatus('started');
    try {
      await api.post('/todo/apply-draft', { llm_node_id: nodeId });
      pollApplyStatus(nodeId);
    } catch (err) {
      // Applied already from another tab: follow that merge.
      if (err?.response?.data?.code === 'todo_merge_started') {
        pollApplyStatus(nodeId);
        return;
      }
      const msg = err?.response?.data?.error || 'Todo update failed';
      setApplyStatus('error');
      setApplyError(msg);
      // Nothing started, so the proposal is still pending, unless the
      // server found no pending proposal (404).
      setCanApplyAgain(err?.response?.status !== 404);
    }
  }, [nodeId, pollApplyStatus]);

  const handleCreateIssue = useCallback(async () => {
    if (!nodeId) return;
    setIssueApplyStatus('started');
    try {
      const res = await api.post('/github/create-issue', { llm_node_id: nodeId });
      setIssueApplyStatus('completed');
      setIssueResult(res.data);
      onApplied?.('propose_github_issue', {
        apply_status: 'completed',
        issue_url: res.data?.issue_url,
        issue_number: res.data?.issue_number,
      });
    } catch (err) {
      const msg = err?.response?.data?.error || 'Issue creation failed';
      setIssueApplyStatus('error');
      setIssueApplyError(msg);
    }
  }, [nodeId, onApplied]);

  const handleSendFeedback = useCallback(async () => {
    if (!nodeId) return;
    setFeedbackApplyStatus('started');
    try {
      const res = await api.post('/feedback/submit', { llm_node_id: nodeId });
      setFeedbackApplyStatus('completed');
      onApplied?.('propose_feedback', {
        apply_status: 'completed', feedback_id: res.data?.feedback_id });
    } catch (err) {
      const msg = err?.response?.data?.error || 'Feedback send failed';
      setFeedbackApplyStatus('error');
      setFeedbackApplyError(msg);
    }
  }, [nodeId, onApplied]);

  const handleSaveShare = useCallback(async (idx) => {
    if (!nodeId) return;
    setShareStates(s => ({ ...s, [idx]: 'started' }));
    try {
      const res = await api.post('/share/save-proposal',
        { node_id: nodeId, share_index: idx });
      setShareStates(s => ({ ...s, [idx]: 'completed' }));
      onApplied?.('propose_share', {
        apply_status: res.data?.status === 'completed' ? 'completed' : 'partial',
        share_id: res.data?.share?.id,
        saved_indexes: res.data?.saved_indexes,
      });
    } catch (err) {
      const msg = err?.response?.data?.error || 'Saving the share failed';
      setShareStates(s => ({ ...s, [idx]: 'error' }));
      setShareErrors(e => ({ ...e, [idx]: msg }));
    }
  }, [nodeId, onApplied]);

  // A block is saved if this session saved it, or the server meta says so.
  const shareStateFor = (idx) => shareStates[idx]
    || ((metaSavedShares === 'all'
      || (Array.isArray(metaSavedShares) && metaSavedShares.includes(idx)))
      ? 'completed' : null);

  const hasTodoUpdate = !shareOnly
    && (hasTodo || toolCallsMeta?.some(tc => tc.name === 'propose_todo'));
  const hasGithubIssue = !shareOnly
    && (hasIssue || toolCallsMeta?.some(tc => tc.name === 'propose_github_issue'));
  const hasFeedbackProposal = !shareOnly
    && (hasFeedback || toolCallsMeta?.some(tc => tc.name === 'propose_feedback'));
  const hasShareProposal = hasShare || toolCallsMeta?.some(tc => tc.name === 'propose_share');
  const hasPrefsUpdate = !shareOnly && toolCallsMeta?.some(
    tc => tc.name === 'update_ai_preferences' && tc.status === 'success'
  );

  if (!hasTodoUpdate && !hasGithubIssue && !hasFeedbackProposal && !hasShareProposal && !hasPrefsUpdate) return null;

  const StatusTag = styles.statusTag;
  const sectionLabel = (text) => (
    <div style={styles.sectionLabel}>
      {text}
    </div>
  );

  return (
    <div style={styles.wrapper}>
      {hasTodo && (
        <div>
          {(parsed.completed || parsed.newTasks) && (
            <div style={styles.sectionWrapper}>
              {sectionLabel('Updated from your sharing')}
              {parsed.completed && parseTodoItems(parsed.completed).map((item, i) => (
                <ProposalTaskRow
                  key={`done-${i}`}
                  item={item}
                  styles={styles}
                  toggleable={toggleable}
                  variant="completed"
                  addingKey={addingKey}
                  setAddingKey={setAddingKey}
                  onToggle={handleToggleItem}
                  onInsert={handleInsertAfter}
                />
              ))}
              {parsed.newTasks && parseTodoItems(parsed.newTasks).map((item, i) => (
                <ProposalTaskRow
                  key={`new-${i}`}
                  item={item}
                  styles={styles}
                  toggleable={toggleable}
                  variant="new"
                  addingKey={addingKey}
                  setAddingKey={setAddingKey}
                  onToggle={handleToggleItem}
                  onInsert={handleInsertAfter}
                />
              ))}
            </div>
          )}

          {parsed.priority && (
            <div style={styles.sectionWrapper}>
              {sectionLabel('Suggested priority order')}
              {parsePriorityItems(parsed.priority).map((item, i) => (
                <div key={`pri-${i}`} style={styles.priorityCard}>
                  <span style={styles.priorityNumber}>{i + 1}</span>
                  <span style={styles.priorityText}>{item.text}</span>
                  {item.hint && (
                    <span style={styles.priorityHint}>{item.hint}</span>
                  )}
                  {styles.roomy && (
                    <span style={{
                      color: 'var(--text-muted)', opacity: 0.3,
                      fontSize: '0.9rem', letterSpacing: '2px', flexShrink: 0,
                    }}>⋮⋮</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {parsed.note && (
            <div style={styles.noteText}>
              <span style={{ color: 'var(--text-secondary)' }}>A note: </span>
              {parsed.note}
            </div>
          )}
        </div>
      )}

      {hasTodoUpdate && canAct && (
        <div style={styles.applyWrapper}>
          {!applyStatus && (
            <button onClick={handleApplyTodo} style={styles.button}>
              Apply changes to my Todo
            </button>
          )}
          {applyStatus === 'started' && (
            <StatusTag style={{ ...styles.statusText, color: 'var(--text-muted)' }}>
              Todo update started…
            </StatusTag>
          )}
          {applyStatus === 'completed' && (
            <StatusTag style={{ ...styles.statusText, color: 'var(--success)' }}>
              Todo updated
            </StatusTag>
          )}
          {applyStatus === 'error' && (
            <StatusTag style={{ ...styles.statusText, color: 'var(--accent)' }}>
              {applyError || 'Todo update failed'}
            </StatusTag>
          )}
          {applyStatus === 'error' && canApplyAgain && (
            <button
              onClick={handleApplyTodo}
              style={{ ...styles.button, marginLeft: styles.roomy ? 0 : '10px' }}
            >
              Apply again
            </button>
          )}
        </div>
      )}

      {hasGithubIssue && parsed.issueTitle && (
        <div style={styles.issueWrapper}>
          {sectionLabel(styles.roomy ? 'GitHub Issue Proposal' : 'GitHub Issue')}
          <div style={styles.issueCard}>
            <h3 style={styles.issueTitleStyle}>{parsed.issueTitle}</h3>
            {parsed.issueDescription && (
              <MarkdownBody
                style={styles.issueDescStyle}
                paragraphMargin={styles.roomy ? '0 0 8px 0' : '0 0 6px 0'}
              >
                {parsed.issueDescription}
              </MarkdownBody>
            )}
            {parsed.issueCategory && (
              <span style={styles.issueCategory}>{parsed.issueCategory}</span>
            )}
          </div>
          {canAct && (
          <div style={styles.issueButtonWrapper}>
            {!issueApplyStatus && (
              <button onClick={handleCreateIssue} style={styles.button}>
                {styles.roomy ? 'Create GitHub Issue' : 'Create issue'}
              </button>
            )}
            {issueApplyStatus === 'started' && (
              <StatusTag style={{ ...styles.statusText, color: 'var(--text-muted)' }}>
                Creating issue…
              </StatusTag>
            )}
            {issueApplyStatus === 'completed' && (
              <StatusTag style={{ ...styles.statusText, color: 'var(--success)' }}>
                Issue created{issueResult?.issue_url && (
                  <> — <a
                    href={issueResult.issue_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--success)', textDecoration: 'underline' }}
                  >#{issueResult.issue_number}</a></>
                )}
              </StatusTag>
            )}
            {issueApplyStatus === 'error' && (
              <StatusTag style={{ ...styles.statusText, color: 'var(--accent)' }}>
                {issueApplyError || 'Issue creation failed'}
              </StatusTag>
            )}
          </div>
          )}
        </div>
      )}

      {hasFeedbackProposal && parsed.feedback && (
        <div style={styles.issueWrapper}>
          {sectionLabel(styles.roomy ? 'Feedback for the Team' : 'Feedback')}
          <div style={styles.issueCard}>
            <MarkdownBody
              style={styles.issueDescStyle}
              paragraphMargin={styles.roomy ? '0 0 8px 0' : '0 0 6px 0'}
            >
              {parsed.feedback}
            </MarkdownBody>
            {parsed.feedbackCategory && (
              <span style={styles.issueCategory}>{parsed.feedbackCategory}</span>
            )}
          </div>
          {canAct && (
          <div style={styles.issueButtonWrapper}>
            {!feedbackApplyStatus && (
              <button onClick={handleSendFeedback} style={styles.button}>
                {styles.roomy ? 'Send feedback to the team' : 'Send feedback'}
              </button>
            )}
            {feedbackApplyStatus === 'started' && (
              <StatusTag style={{ ...styles.statusText, color: 'var(--text-muted)' }}>
                Sending…
              </StatusTag>
            )}
            {feedbackApplyStatus === 'completed' && (
              <StatusTag style={{ ...styles.statusText, color: 'var(--success)' }}>
                Feedback sent — thank you
              </StatusTag>
            )}
            {feedbackApplyStatus === 'error' && (
              <StatusTag style={{ ...styles.statusText, color: 'var(--accent)' }}>
                {feedbackApplyError || 'Feedback send failed'}
              </StatusTag>
            )}
          </div>
          )}
        </div>
      )}

      {hasShareProposal && shares.length > 0 && (
        <div style={styles.issueWrapper}>
          {sectionLabel(styles.roomy
            ? (shares.length > 1 ? 'Proposed Shares' : 'Proposed Share')
            : (shares.length > 1 ? 'Shares' : 'Share'))}
          {shares.map((share, idx) => {
            const state = shareStateFor(idx);
            return (
            <div key={idx} style={idx > 0 ? { marginTop: '14px' } : undefined}>
            <div
              style={{
                ...styles.issueCard,
                position: 'relative',
              }}
            >
              {/* One-click copy for pasting the share elsewhere (e.g. X). */}
              <CopyButton
                text={share.content}
                title="Copy share text"
                style={{ position: 'absolute', top: '8px', right: '8px' }}
              />
              <MarkdownBody
                style={styles.issueDescStyle}
                paragraphMargin={styles.roomy ? '0 0 8px 0' : '0 0 6px 0'}
              >
                {share.content}
              </MarkdownBody>
              {share.type && (
                <span style={styles.issueCategory}>{share.type}</span>
              )}
            </div>
            {canAct && (
            <div style={styles.issueButtonWrapper}>
              {!state && (
                <button onClick={() => handleSaveShare(idx)} style={styles.button}>
                  {styles.roomy ? 'Save to your shares' : 'Save to shares'}
                </button>
              )}
              {state === 'started' && (
                <StatusTag style={{ ...styles.statusText, color: 'var(--text-muted)' }}>
                  Saving…
                </StatusTag>
              )}
              {state === 'completed' && (
                <StatusTag style={{ ...styles.statusText, color: 'var(--text-muted)' }}>
                  <span style={{ color: 'var(--success)' }}>Saved as a private draft</span>
                  {' — publish from your '}
                  <Link to="/share" style={{ color: 'var(--accent)', textDecoration: 'none' }}>
                    Share page
                  </Link>
                </StatusTag>
              )}
              {state === 'error' && (
                <StatusTag style={{ ...styles.statusText, color: 'var(--accent)' }}>
                  {shareErrors[idx] || 'Saving the share failed'}
                </StatusTag>
              )}
            </div>
            )}
            </div>
            );
          })}
        </div>
      )}

      {hasPrefsUpdate && (
        <p style={{
          fontFamily: 'var(--sans)', fontSize: '0.75rem', fontWeight: 300,
          color: 'var(--success)',
          textAlign: styles.roomy ? 'center' : 'left',
          marginTop: '16px',
        }}>
          Preferences updated
        </p>
      )}
    </div>
  );
}
